/**
 * Runtime v3 — ONE CLAIM, AS THE EXECUTOR PUBLISHES IT (I14-06, #8149).
 *
 * Design v2 §3 `claim.ts`: "one proposition, shared by persistence and
 * comparison". That sharing is the whole point of this module, and it is why
 * the proposition below is not a new shape.
 *
 * THERE IS ONE CANON, AND IT ALREADY EXISTS. `canonicalAnalyticalClaims`
 * (`analytical-resolution.ts`) is the form two runs of one question are
 * compared in, and it has been corrected five times for identity defects that
 * only a comparison canon can find. A second, persistence-only spelling of a
 * claim would be a second thing to keep in step with those corrections, and
 * the failure mode would be silent: the ledger would store a claim the replay
 * comparison could not see a change in. So `RuntimeV3ClaimProposition` IS an
 * entry of that canon, widened by exactly one field.
 *
 * WHY `periodWindow` AND NOT `period`. The canon already emits `period` — the
 * period LABEL the producer stated the figure under. A claim also needs the
 * concrete interval it is true over, its time basis, and whether the reader
 * chose that interval or the declaration did; those are the things a replay at
 * another moment, or under another default rule, must compare unequal on.
 * Writing them into `period` would have OVERWRITTEN a canon field with a
 * different kind of value — a silent loss of the label from every stored
 * claim, and a canon whose entries no longer matched the comparison's. The
 * window therefore sits beside the label under its own name.
 */

import type { ContentDigest, IsoDate } from './common';
import type { RuntimeV3AnalyticalOperation, RuntimeV3DecisionOrigin } from './analytical-request';
import type { RuntimeV3OperatorVersionRef } from './runs';
import type { RuntimeV3ResolvedMeaning } from './resolved-meaning';
import type { canonicalAnalyticalClaims } from './analytical-resolution';
// Desk ruling on the I14-00/I14-01 fold: `operation-registry.ts` OWNS
// `RUNTIME_V3_PERIOD_BASES` and `RuntimeV3PeriodBasis`; `definitions.ts`
// imports it from there and does not re-declare it, so this reads it from the
// one module that declares it rather than through a re-export that moved.
import type { RuntimeV3PeriodBasis } from './operation-registry';

/**
 * WHAT PART AN ANSWER A CLAIM PLAYS IN IT.
 *
 * Assigned STRUCTURALLY by the executor — a grouped cell is a row, an
 * ungrouped figure is a lead — never by reading a label. The render roles
 * (which lead goes first, which disclosure prints where) are I14-07's; this
 * is the producer's own statement of what kind of figure it published, and it
 * is closed so a renderer can be exhaustive over it.
 */
export const RUNTIME_V3_CLAIM_ROLES = [
  'lead', 'row', 'aggregate', 'disclosure', 'assumption', 'passage',
] as const;
export type RuntimeV3ClaimRole = (typeof RUNTIME_V3_CLAIM_ROLES)[number];

/**
 * The concrete interval a claim is true over, and who chose it.
 *
 * `basis` IS NULLABLE, AND THE NULL IS THE HONEST ANSWER. The time basis of a
 * figure — flow, point in time, annualised run rate — is DECLARED per measure
 * (design v2 §3, `RuntimeV3MeasurePeriodDeclaration`), and the executor reads
 * it, it does not infer it. Until the validator threads that declaration
 * through to the resolution, the executor has no basis to state, and stating
 * one anyway would be exactly the invented default the whole request/
 * resolution split exists to prevent. Absence is published as absence, the
 * same rule `RuntimeV3ResultGap.kind` follows.
 */
export type RuntimeV3ClaimPeriodWindow = Readonly<{
  start: IsoDate;
  end: IsoDate;
  /** Reference window for a derived two-period comparison, when present. */
  comparisonWindows?: readonly Readonly<{ start: IsoDate; end: IsoDate }>[];
  /** Zero-based chronological position in the resolved period decision. */
  position: number;
  basis: RuntimeV3PeriodBasis | null;
  origin: RuntimeV3DecisionOrigin;
  /**
   * #8282 — TRUE WHEN `start`/`end` ARE THE COVERED INTERSECTION, NOT THE
   * WINDOW THE READER NAMED.
   *
   * A binding projection reads this rather than re-deriving it from the
   * requested period: `start`/`end` are already the covered window when this
   * is true, so a downstream reader never has to guess which of the two
   * spans a partial claim's own interval is.
   */
  isPartial: boolean;
}>;

/**
 * A COMPLETE PROPOSITION ABOUT ONE FIGURE — the shared canon, plus its window.
 *
 * Every field of the comparison canon (metric, result class, claim domain,
 * unit, exact value, period label, grain, scope, qualification, method,
 * denominator, coverage, exclusions) is present by construction, because this
 * type IS that canon's entry; a field added there is in this type the moment
 * it exists, with no second declaration to update.
 */
export type RuntimeV3ClaimProposition =
  ReturnType<typeof canonicalAnalyticalClaims>[number]
  & Readonly<{ periodWindow: RuntimeV3ClaimPeriodWindow }>;

/**
 * THE DATA IDENTITY ONE CLAIM RESTS ON — dataset, revision and its digest.
 *
 * The digest and not only the id, because a revision id is a handle and a
 * digest is what a replay can be judged unequal on; and the dataset ref beside
 * them, because two datasets may pin revisions of the same version number.
 * Narrower than `RuntimeV3RevisionRef` on purpose: the tenant is not part of
 * what produced a figure, it is who may see it, and it is already the scope
 * every read here was authorized under.
 */
export type RuntimeV3ClaimRevisionRef = Readonly<{
  datasetRef: string;
  revisionId: string;
  digest: ContentDigest;
}>;

/**
 * WHERE THE FIGURE CAME FROM, ATTACHED AT COMPUTATION TIME.
 *
 * Not reconstructed later from the run store: provenance assembled after the
 * fact is provenance that can be assembled wrongly, and the anti-fabrication
 * fence's whole claim is that a rendered number is bound to the computation
 * that produced it. Every arm names a governed producer; there is no arm for
 * a figure that has none, which is what makes "a claim without provenance" an
 * unrepresentable state rather than a validation rule.
 *
 * WHY THE COMPUTATION ARM CARRIES MORE THAN A RUN ID (#8149 HOLD F3). A run id
 * and a plan digest name the computation but not what it was computed OVER: a
 * consumer holding only those must re-query the run store to learn which
 * revision a figure came from, which is exactly the after-the-fact reassembly
 * this module's header forbids. So the arm names the pinned revisions, the
 * operation and the registered operator version that served it, and the
 * members the figure was resolved over. `operator` is nullable because a
 * requested operation the registry does not register HAS no operator version,
 * and an absent one is published as absent rather than invented.
 */
export type RuntimeV3ClaimProvenance =
  | Readonly<{
    kind: 'governed_computation';
    runId: string;
    queryHash: ContentDigest;
    revisions: readonly RuntimeV3ClaimRevisionRef[];
    operation: RuntimeV3AnalyticalOperation;
    operator: RuntimeV3OperatorVersionRef | null;
    memberRefs: readonly string[];
    meaning?: RuntimeV3ResolvedMeaning;
  }>
  | Readonly<{ kind: 'governed_derivation'; runId: string; inputRunIds: readonly string[] }>
  | Readonly<{ kind: 'governed_document'; documentRef: string; revisionId: string; anchor: string }>
  | Readonly<{ kind: 'scenario_assumption'; runId: string; parameterName: string }>;

/**
 * One published claim: what was asserted, what part it plays, and what proves
 * it.
 *
 * `claimId` is DERIVED, never minted from a counter or a clock: an answer
 * rebuilt from the same runs must carry the same claim ids, or the
 * five-execution byte-equality measure compares two answers that differ only
 * in handles. `passageText` is present only on the `passage` role, where the
 * claim IS the quoted text (document_lookup); a figure never carries one.
 */
/**
 * WHAT KIND OF ASSERTION A CLAIM IS.
 *
 * Every claim the executor publishes today is `computed`: a figure a governed
 * operator produced. The other three exist so that the moment something else
 * is published beside them it has to SAY SO in the type rather than arrive
 * looking identical — an `interpretation` ("this reflects deferred hiring"), a
 * `hypothesis`, or a `proposed_action` is a sentence about the world, not a
 * measurement of it, and the failure mode these guard against is the one a
 * reader cannot detect: a recommendation rendered in the same typeface, with
 * the same decimal places, as an audited figure.
 *
 * The renderer's rule follows from that and is enforced in
 * `exhibit-render-manifest.ts`: ONLY a `computed` claim may reach a figure
 * token. A non-computed claim still renders — suppression is retired — but it
 * renders as what it is, never as a number the reader may bank.
 */
export const RUNTIME_V3_CLAIM_STATUSES = [
  'computed', 'interpretation', 'hypothesis', 'proposed_action', 'retrieved',
] as const;
export type RuntimeV3ClaimStatus = (typeof RUNTIME_V3_CLAIM_STATUSES)[number];

export type RuntimeV3NumericClaimRecord = Readonly<{
  claimId: string;
  /** A retrieved passage is never an engine-bound numeric claim. */
  status: Exclude<RuntimeV3ClaimStatus, 'retrieved'>;
  answerId: string;
  outputId: string;
  role: RuntimeV3ClaimRole;
  proposition: RuntimeV3ClaimProposition;
  provenance: RuntimeV3ClaimProvenance;
  passageText?: string;
}>;

/** Text has no numerical proposition or fabricated financial period. */
export type RuntimeV3TextClaimRecord = Readonly<{
  claimId: string;
  answerId: string;
  outputId: string;
  status: 'retrieved';
  role: 'passage';
  passageText: string;
  proposition: Readonly<{
    subjectRef: string;
    fieldRef: string;
    contentDigest: ContentDigest;
    sourceEffectiveAt: string | null;
  }>;
  provenance: Extract<RuntimeV3ClaimProvenance, { kind: 'governed_document' }>
    & Readonly<{
      digest: ContentDigest;
      version: number;
      sourceRefs: readonly Readonly<Record<string, unknown>>[];
      posture: Readonly<Record<string, unknown>>;
    }>;
}>;

export type RuntimeV3ClaimRecord = RuntimeV3NumericClaimRecord | RuntimeV3TextClaimRecord;

export function isRuntimeV3NumericClaim(
  claim: RuntimeV3ClaimRecord,
): claim is RuntimeV3NumericClaimRecord {
  return claim.status !== 'retrieved';
}

/**
 * WHICH BUDGET A CLAIM'S ROLE COUNTS AGAINST (claim-budget role split, brief
 * "the claim budget caps what the user asked for, not the honesty
 * disclosures the engine adds").
 *
 * `requested` is what the reader's own operations produce -- the figures
 * that ARE the answer to the question asked: `lead`, `row` and `aggregate`
 * (every analytical figure role), and `passage` (the one role a
 * `document_lookup` request's own answer IS, priced by its own
 * `RUNTIME_V3_DOCUMENT_LOOKUP_CLAIM_COST`). This class is checked against
 * `RuntimeV3InterpretedRequest['claimBudget']`, unchanged -- sized from the
 * DRAFT's own requested outputs before any execution ever runs, exactly as
 * strict as before this split existed.
 *
 * `disclosure` is what the ENGINE adds on its own for honesty: `disclosure`
 * (a non-additive breakdown line, a coverage gap, an exclusion) and
 * `assumption` (a scenario input the reader did not supply a figure for).
 * Neither was ever part of what was asked, so counting them against the
 * REQUESTED budget refused a whole turn over an expansion the reader's own
 * question never sized -- a single-total request whose draft-time budget is
 * 1 refused outright the moment the executor legitimately disclosed a
 * handful of non-additive figures beside it. This class is checked against
 * its OWN small, central cap instead
 * (`RUNTIME_V3_CLAIM_BUDGET_CAPS.maxDisclosureClaimsPerAnswer`,
 * `slot-policy.ts`) and never refuses the turn -- see
 * `interpreted-arm.service.ts` (the requested check) and `published-turn.ts`
 * (the disclosure cap's own typed gap sentence, never a trim).
 *
 * A TOTAL MAP, ON PURPOSE: a role added to `RUNTIME_V3_CLAIM_ROLES` without
 * an entry here is a compile error, so a future role -- a UI-lane
 * "companion" output among them -- cannot silently fall into either budget
 * by default; it has to be classified here first.
 */
export const RUNTIME_V3_CLAIM_BUDGET_CLASSES = ['requested', 'disclosure'] as const;
export type RuntimeV3ClaimBudgetClass = (typeof RUNTIME_V3_CLAIM_BUDGET_CLASSES)[number];

const CLAIM_BUDGET_CLASS_BY_ROLE: Readonly<Record<RuntimeV3ClaimRole, RuntimeV3ClaimBudgetClass>> =
  Object.freeze({
    lead: 'requested',
    row: 'requested',
    aggregate: 'requested',
    passage: 'requested',
    disclosure: 'disclosure',
    assumption: 'disclosure',
  });

/** The budget class one claim role counts against. Total over the closed role union. */
export function runtimeV3ClaimBudgetClass(role: RuntimeV3ClaimRole): RuntimeV3ClaimBudgetClass {
  return CLAIM_BUDGET_CLASS_BY_ROLE[role];
}

/**
 * WHICH TIER A CLAIM'S ROLE RENDERS IN, IN THE ANSWER TEXT (writer 23,
 * 2026-09-24 brief). LOWER RENDERS FIRST. `lead` and `aggregate` are
 * requested TOTALS — the direct answer to what was asked, whichever shape
 * the operation produced — and always lead; `row` is one grouped cell of
 * that same requested answer and follows. `disclosure` and `assumption`
 * are the engine's own honesty additions (the SAME split
 * `runtimeV3ClaimBudgetClass` above already draws: neither is what the
 * reader asked for) and never outrank a requested figure. `passage` is a
 * document_lookup's own answer text — a different shape of answer
 * entirely, never one of the numeric bindings this tier orders — and
 * sorts last so the map stays TOTAL: a role added to
 * `RUNTIME_V3_CLAIM_ROLES` without an entry here is a compile error,
 * exactly the same discipline `CLAIM_BUDGET_CLASS_BY_ROLE` above already
 * enforces.
 */
const CLAIM_RENDER_TIER_BY_ROLE: Readonly<Record<RuntimeV3ClaimRole, number>> = Object.freeze({
  lead: 0,
  aggregate: 0,
  row: 1,
  disclosure: 2,
  assumption: 2,
  passage: 3,
});

/** The tier one claim role renders in the answer text; lower renders first.
 *  Total over the closed role union — never a phrase, never a per-producer branch. */
export function runtimeV3ClaimRenderTier(role: RuntimeV3ClaimRole): number {
  return CLAIM_RENDER_TIER_BY_ROLE[role];
}

/**
 * Every claim, split by which budget its role counts against. Order is
 * preserved within each part -- neither part is sorted or deduplicated,
 * because a budget check reads only the count and a renderer that later
 * reads either part must see the claims in the order the executor
 * published them.
 */
export function runtimeV3PartitionClaimsByBudgetClass(
  claims: readonly RuntimeV3ClaimRecord[],
): Readonly<{
  requested: readonly RuntimeV3ClaimRecord[];
  disclosure: readonly RuntimeV3ClaimRecord[];
}> {
  const requested: RuntimeV3ClaimRecord[] = [];
  const disclosure: RuntimeV3ClaimRecord[] = [];
  for (const claim of claims) {
    (runtimeV3ClaimBudgetClass(claim.role) === 'requested' ? requested : disclosure).push(claim);
  }
  return Object.freeze({ requested: Object.freeze(requested), disclosure: Object.freeze(disclosure) });
}
